import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HostEstimator from "./HostEstimator";
import { API_BASE_URL } from "../../config";
import "../../styles/Waitlist.css";

// Become a host.
//
// Hosting is invitation-only while hardware is deployed a few sites at a time,
// so this is the front door: anyone can apply, and an approved entry is what
// lets that email address register.
//
// --- the order of this page IS the design ----------------------------------
//
// It used to open with the application form. The first thing a business owner
// saw was a request for their name and email in exchange for a place on a list
// -- an ask before any answer, which is the weakest possible opening and reads
// like every other startup waitlist.
//
// It now runs: hook -> estimate -> result -> explanation -> application.
//
// The estimator leads because it is the one interaction that changes how
// somebody thinks about their own wall. Four dropdowns, no address, no
// sign-up, and at the end a number attached to THEIR room. That is what turns
// "what is this?" into "wait, that wall could earn?", and only then is it
// worth asking who they are.
//
// Every answer carries into the application, so the two halves are one
// process. Re-asking the venue type after they have just told the estimator is
// what makes a funnel feel like two unrelated forms.
//
// Nothing is stored until the application is submitted. Playing with the
// estimator still creates no record of anybody.

const STEPS = [
    {
        n: "01",
        title: "Tell us about your space",
        body:
            "What kind of business you run, roughly how many people come " +
            "through, and where a screen could go.",
    },
    {
        n: "02",
        title: "Get set up",
        body:
            "We are onboarding locations a few at a time, so every screen is " +
            "installed and configured properly.",
    },
    {
        n: "03",
        title: "Start earning",
        body:
            "Advertisers can include your screen in their campaigns, and you " +
            "earn when their ads actually run.",
    },
];

const BENEFITS = [
    {
        title: "Earn from space you already have",
        body: "An empty wall costs you nothing today, and makes you nothing either.",
    },
    {
        title: "Your audience is already there",
        body: "People waiting, eating, or between appointments — time nobody is selling.",
    },
    {
        title: "You approve what appears",
        body: "Every ad is reviewed by you before it plays in your business.",
    },
    {
        title: "Almost nothing to do after setup",
        body: "The screen looks after itself. You set your rate and withdraw what it earns.",
    },
];

export default function JoinWaitlist() {
    const [form, setForm] = useState({
        display_name: "",
        email: "",
        business_name: "",
        address: "",
        note: "",
    });
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [searchParams] = useSearchParams();
    const applyRef = useRef(null);
    // What the estimator was told, kept so the application can send it on and
    // so the form can show it back rather than asking for it again.
    const [estimated, setEstimated] = useState(null);

    const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // /estimate has always linked here as `/waitlist?address=...` and this page
    // never read it -- so somebody who priced their salon, clicked through, and
    // was asked for the address again was being asked to retype something they
    // had already given us. Only ever fills an untouched field.
    useEffect(() => {
        const handed = searchParams.get("address");
        if (!handed) return;
        setForm((c) => (c.address ? c : { ...c, address: handed }));
    }, [searchParams]);

    // The estimator's CTA: keep the answers, move the reader to the form.
    function startApplication(answers) {
        setEstimated(answers);
        if (applyRef.current) {
            applyRef.current.scrollIntoView({
                // Respects the OS setting; a jump is fine, a jolt is not.
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
                    ? "auto"
                    : "smooth",
                block: "start",
            });
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            // The estimator's answers ride along in the note. That is the field
            // the waitlist model already has, so this needs no migration to be
            // useful -- and those answers are what decide whether a location is
            // worth putting scarce hardware in, so dropping them here would
            // throw away the most valuable thing this page collects.
            const payload = { ...form };
            if (estimated) {
                const extra = [
                    estimated.venue_label && `Venue: ${estimated.venue_label}`,
                    estimated.visitors_label && `Weekly visitors: ${estimated.visitors_label}`,
                    estimated.dwell_label && `Typical visit: ${estimated.dwell_label}`,
                    estimated.screen_inches && `Screen: ${estimated.screen_inches}"`,
                ].filter(Boolean).join(" · ");
                payload.note = payload.note
                    ? `${payload.note}\n\n[From the estimator] ${extra}`
                    : `[From the estimator] ${extra}`;
            }
            const res = await fetch(`${API_BASE_URL}/api/waitlist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "Could not add you to the list.");
                setSubmitting(false);
                return;
            }
            setResult(data);
        } catch {
            setError("Network error. You were not added to the list.");
        }
        setSubmitting(false);
    }

    return (
        <div className="hp">
            {/* 1 — HERO. Value proposition left, estimator right, as one thing.
                The estimator is the hero's right-hand side rather than a
                section below it: a calculator you have to scroll to find is a
                calculator most people never touch. */}
            <section className="hp__hero">
                <div className="hp__hero-inner">
                    <div className="hp__pitch">
                        <p className="hp__eyebrow">For host businesses</p>
                        <h1 className="hp__title">
                            Got a wall?<br />Put it to work.
                        </h1>
                        <p className="hp__lede">
                            A waiting room, lobby, seating area — if people spend
                            time there, it could earn.
                        </p>
                        <p className="hp__nudge">
                            See what yours is worth. No sign-up, no address.
                        </p>
                    </div>

                    <div className="hp__estimator">
                        <HostEstimator onApply={startApplication} />
                    </div>
                </div>
            </section>

            {/* 2 — HOW HOSTING WORKS */}
            <section className="hp__how">
                <div className="hp__band">
                    <header className="hp__band-head">
                        <h2 className="hp__h2">
                            You bring the wall.<br />We bring the advertisers.
                        </h2>
                        <p className="hp__band-lede">
                            WallBurst connects your audience with businesses that want
                            to reach them. You provide the space, we handle the
                            platform.
                        </p>
                    </header>

                    {/* Numbered because it genuinely is a sequence: nobody is set
                        up before applying, and nobody earns before being set up. */}
                    <ol className="hp__steps">
                        {STEPS.map((step) => (
                            <li className="hp__step" key={step.n}>
                                <span className="hp__n" aria-hidden="true">{step.n}</span>
                                <h3>{step.title}</h3>
                                <p>{step.body}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* 3 — WHY HOST */}
            <section className="hp__why">
                <div className="hp__band">
                    <h2 className="hp__h2 hp__h2--center">Why businesses host</h2>
                    <ul className="hp__benefits">
                        {BENEFITS.map((b) => (
                            <li className="hp__benefit" key={b.title}>
                                <h3>{b.title}</h3>
                                <p>{b.body}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* 4 — APPLICATION. Last, and this is the first point the page asks
                for an address: by now it has given something back. */}
            <section className="hp__apply" id="apply" ref={applyRef}>
                <div className="hp__apply-inner">
                  <div className="hp__panel">
                    {result ? (
                        <div className="hp__done">
                            <div className="hp__done-mark" aria-hidden="true">✓</div>
                            <h2 className="hp__h2">You’re on the list.</h2>
                            <p className="hp__done-msg">{result.message}</p>

                            {result.priority ? (
                                <div className="wb-note wb-note--ok">
                                    You gave us a location, so you are near the front of
                                    the queue — we plan deployments geographically, and
                                    knowing where the screen would go is what lets us
                                    plan the next one.
                                </div>
                            ) : (
                                <div className="wb-note">
                                    Adding roughly where the screen would go moves you up
                                    the list. You can{" "}
                                    <button
                                        type="button"
                                        className="hp__inline-btn"
                                        onClick={() => setResult(null)}
                                    >
                                        add it now
                                    </button>{" "}
                                    — resubmitting only ever adds information.
                                </div>
                            )}

                            <div className="hp__done-actions">
                                <Link to="/" className="wb-btn wb-btn--secondary wb-btn--lg">
                                    Back to home
                                </Link>
                                <Link to="/devices" className="wb-btn wb-btn--ghost wb-btn--lg">
                                    See what is listed
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <header className="hp__apply-head">
                                <h2 className="hp__h2">Ready to put your wall to work?</h2>
                                <p>
                                    We’re onboarding locations a few at a time. Tell us
                                    where you’d like to host a screen and we’ll reach out
                                    as we expand in your area.
                                </p>
                            </header>

                            <form className="hp__form" onSubmit={handleSubmit}>
                                {/* What the estimator already knows, shown rather
                                    than re-asked. Visible so it is obvious the
                                    answers carried over, and so nobody has to
                                    wonder what is being sent on their behalf. */}
                                {estimated && (
                                    <div className="hp__carried">
                                        <span className="hp__carried-tag">From your estimate</span>
                                        <p>
                                            {[
                                                estimated.venue_label,
                                                estimated.visitors_label,
                                                estimated.screen_inches && `${estimated.screen_inches}" screen`,
                                            ].filter(Boolean).join(" · ")}
                                            . We’ll send this with your application — no need
                                            to type it again.
                                        </p>
                                    </div>
                                )}

                                <div className="hp__form-row">
                                    <label className="wb-field">
                                        <span className="wb-label">Your name</span>
                                        <input
                                            className="wb-input" name="display_name" autoComplete="name"
                                            required value={form.display_name} onChange={change}
                                        />
                                    </label>

                                    <label className="wb-field">
                                        <span className="wb-label">Email</span>
                                        <input
                                            className="wb-input" name="email" type="email"
                                            autoComplete="email"
                                            required value={form.email} onChange={change}
                                        />
                                        <span className="wb-hint">
                                            Your invitation is tied to this address, so use the
                                            one you will sign up with.
                                        </span>
                                    </label>
                                </div>

                                <label className="wb-field">
                                    <span className="wb-label">
                                        Business name <em>(optional)</em>
                                    </span>
                                    <input
                                        className="wb-input" name="business_name"
                                        autoComplete="organization"
                                        value={form.business_name} onChange={change}
                                    />
                                </label>

                                {/* The one answer on this form that changes the
                                    outcome, so the only one with a surface of its
                                    own. Spending emphasis evenly would spend it on
                                    nothing. */}
                                <div className="hp__priority">
                                    <span className="hp__priority-tag">Moves you up the list</span>
                                    <label className="wb-field">
                                        <span className="wb-label">
                                            Where is your business? <em>(optional)</em>
                                        </span>
                                        <input
                                            className="wb-input" name="address"
                                            autoComplete="street-address"
                                            placeholder="e.g. 700 Main St, Stroudsburg, PA"
                                            value={form.address} onChange={change}
                                        />
                                        <span className="wb-hint">
                                            We deploy geographically, so knowing where the
                                            screen would go is what lets us plan the next
                                            batch. A street and town is plenty.
                                        </span>
                                    </label>
                                </div>

                                <label className="wb-field">
                                    <span className="wb-label">
                                        Anything else? <em>(optional)</em>
                                    </span>
                                    <textarea
                                        className="wb-textarea" name="note" rows={3}
                                        placeholder="Where the screen would go, how many screens, anything unusual about the space…"
                                        value={form.note} onChange={change}
                                    />
                                </label>

                                <button
                                    type="submit"
                                    className="wb-btn wb-btn--primary wb-btn--lg wb-btn--block hp__submit"
                                    disabled={submitting}
                                >
                                    {submitting ? "Sending…" : "Apply to host"}
                                </button>

                                {error && (
                                    <div className="wb-note wb-note--danger hp__error">{error}</div>
                                )}

                                <p className="hp__small">
                                    Applying does not create an account, and there is
                                    nothing to sign in to yet. Want to advertise instead?{" "}
                                    <Link to="/register/customer">That is open to everyone</Link>.
                                </p>
                            </form>
                        </>
                    )}
                  </div>
                </div>
            </section>
        </div>
    );
}
